import { Grid } from '@mui/material';
import { RoutalDialog, VSpacer } from '@nx-smartmonkey/ui';
import { useCallback, useEffect, useState } from 'react';

import InfoIcon from '../../assets/img/info-icon.svg';
import { exportToExcel, getStopExamples } from '../../assets/templates/examples';
import { getAddressFields } from '../../components/data-importer/fields/AddressFields';
import { FieldMapping } from '../../components/data-importer/fields/FieldMapping';
import { UploadFile } from '../../components/data-importer/upload-file';
import { useVehicleContext } from '../../context/vehicle/context';
import { PlanFlat } from '../../domain/plan/PlanFlat';
import { useGetMapDefaults } from '../../hooks/map/useGetMapDefaults';
import { useRetrieveActiveProject } from '../../hooks/project/useRetrieveActiveProject';
import { useRetrieveAllSupervisors } from '../../hooks/supervisor/useRetrieveAllSupervisors';
import { useRetrieveSupportDocs } from '../../hooks/support-docs/useRetrieveSupportDocs';
import { useRetrieveUser } from '../../hooks/user/useRetrieveUser';
import { useRetrieveUserCredentials } from '../../hooks/user/useRetrieveUserCredentials';
import Text from '../../react-components/Text';
import eventsPanel from '../../services/events';
import ConfirmDialog from '../ConfirmDialog';
import { GeocodeAddresses } from './GeocodeAddresses';
import { MapStopFields } from './MapStopFields';
import { StepperHeader } from './StepperHeader';
import { UploadStopsData } from './UploadStopsData';
import { CreateStopsList } from './fields/CreateStopsList';
import { getPickupFields } from './fields/PickupFields';
import { getStopFields } from './fields/StopFields';
import { getTaskFields } from './fields/TaskFields';
import { ReviewAddresses } from './review-addresses/ReviewAddresses';

export type ImportStopsDataStage =
  | `load_file`
  | `map_stop_fields`
  | `map_pickup_fields`
  | `map_task_fields`
  | `geocode_addresses`
  | `review_addresses`
  | `upload_data`;

interface DataImporterProps {
  open: boolean;
  activePlan?: PlanFlat;
  handleCloseDialog: (e: any) => void;
  onComplete: () => void;
}

export const DataImporter = ({ open, activePlan, handleCloseDialog, onComplete }: DataImporterProps) => {
  const { getCoordinates } = useGetMapDefaults();
  const { retrieveDoc } = useRetrieveSupportDocs();

  const { data: user } = useRetrieveUser();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: projectSupervisors } = useRetrieveAllSupervisors();
  const { vehicleState } = useVehicleContext();

  const [stage, setStage] = useState<ImportStopsDataStage>(`load_file`);
  const [rawData, setRawData] = useState<any[][]>();

  const [shouldUsePickups, setShouldUsePickups] = useState<boolean>(activeProject?.constraints.pickups || false);
  const [shouldUseTasks, setShouldUseTasks] = useState<boolean>(
    (activeProject?.planner.stopFieldsHasSpecificField(`external_id`) && activeProject?.tasks_enabled) || false
  );

  const [createStopsList, setCreateStopsList] = useState<CreateStopsList>();

  const [showSkipTasks, setShowSkipTasks] = useState<boolean>(false);

  const [stopMapping, setStopMapping] = useState<FieldMapping>();
  const [pickupMapping, setPickupMapping] = useState<FieldMapping>();
  const [tasksMapping, setTasksMapping] = useState<FieldMapping>();

  const cleanState = useCallback(() => {
    setStage(`load_file`);
    setShouldUsePickups(activeProject?.constraints.pickups || false);
    setShouldUseTasks(
      (activeProject?.planner.stopFieldsHasSpecificField(`external_id`) && activeProject?.tasks_enabled) || false
    );
    setRawData(undefined);
    setStopMapping(undefined);
    setPickupMapping(undefined);
    setTasksMapping(undefined);
  }, [activeProject?.tasks_enabled, activeProject?.constraints.pickups, activeProject?.planner]);

  const nextStep = () => {
    // Create the steps
    const steps: ImportStopsDataStage[] = [`load_file`, `map_stop_fields`];
    if (shouldUseTasks) {
      steps.push(`map_task_fields`);
    }
    if (shouldUsePickups) {
      steps.push(`map_pickup_fields`);
    }
    steps.push(`geocode_addresses`);
    steps.push(`review_addresses`);
    steps.push(`upload_data`);

    const currentStepIndex = steps.findIndex((step) => step === stage);
    const nextStep = steps[currentStepIndex + 1];

    setStage(nextStep);
  };

  useEffect(() => {
    if (!shouldUseTasks && rawData && stage === `map_stop_fields`) {
      nextStep();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldUseTasks]);

  const previousStep = useCallback(() => {
    // Create the steps
    const steps: ImportStopsDataStage[] = [`load_file`, `map_stop_fields`];
    if (shouldUseTasks) {
      steps.push(`map_task_fields`);
    }
    if (shouldUsePickups) {
      steps.push(`map_pickup_fields`);
    }
    steps.push(`review_addresses`);
    steps.push(`upload_data`);

    const currentStepIndex = steps.findIndex((step) => step === stage);
    const previousStep = steps[currentStepIndex - 1];

    setStage(previousStep);
  }, [shouldUsePickups, shouldUseTasks, stage]);

  if (!activeProject) return null;

  const canViewSupervisors = userCredentials?.projects.test(activeProject.id, `can_view_supervisors`);

  const error = maxStopsError(rawData);

  function maxStopsError(data: any[][] | undefined): boolean {
    if (!data) return false;

    const totalStops = (activePlan?.total_stops || 0) + (data?.length ? data?.length - 1 : 0);
    const maxStops = activeProject?.managed
      ? activeProject.max_managed_vehicles * 150
      : vehicleState.vehicles.total * 150;

    return totalStops > maxStops;
  }

  return (
    <RoutalDialog
      open={open}
      dialogProps={{
        maxWidth: `xl`,
        fullWidth: true,
        style: {
          paddingRight: 0,
        },
        paperProps: {
          style: {
            overflow: `hidden`,
          },
        },
      }}
      onCloseDialog={(e) => {
        handleCloseDialog(e);
        cleanState();
      }}
      titleContentStyles={{ display: `flex`, flexGrow: 1 }}
      titleContent={
        <Grid container direction="column">
          {stage === `load_file` ? (
            <>
              <Text variant="body-large" intlId="data_importer_dialog.load_file" />

              <VSpacer small />
              <Text
                variant="button"
                onClick={() => {
                  window.open(
                    retrieveDoc({ supportPage: `stopsFromFileV2`, language: user?.language.value }),
                    `_blank`
                  );
                }}
              >
                <Grid container alignItems="center">
                  <img alt="" src={InfoIcon} width="14px" draggable="false" />

                  <div style={{ width: `6px` }} />

                  <Text tagName="span" variant="button" intlId="data_importer_dialog.help_article" />
                </Grid>
              </Text>
            </>
          ) : (
            <StepperHeader
              stage={stage}
              shouldUsePickups={activeProject?.constraints.pickups || false}
              shouldUseTasks={
                (activeProject?.planner.stopFieldsHasSpecificField(`external_id`) && activeProject?.tasks_enabled) ||
                false
              }
            />
          )}
        </Grid>
      }
      showCrossCloseButton={stage === `load_file`}
      showFooter={false}
    >
      <Grid container style={{ height: `calc(80vh - 52px)`, position: `relative` }}>
        {stage === `load_file` && (
          <UploadFile
            onDownloadSampleFile={() => {
              const coordinates = getCoordinates();
              if (activeProject && coordinates) {
                exportToExcel(getStopExamples(coordinates, activeProject), `stops_example`);
              }
            }}
            onDataLoaded={(data) => {
              setRawData(data);
              if (!maxStopsError(data)) nextStep();
            }}
            error={error}
          />
        )}
        {stage === `map_stop_fields` && rawData && (
          <MapStopFields
            id="map_stop_fields"
            rawData={rawData}
            fields={getStopFields({ project: activeProject, projectSupervisors, canViewSupervisors })}
            addressFields={getAddressFields()}
            tasksEnabled={shouldUseTasks}
            continueIntlId="data_importer_dialog.map_stop_fields"
            onCompleted={(mapped) => {
              setStopMapping(mapped);
              if (shouldUseTasks && !mapped.getById(`external_id`)) {
                setShowSkipTasks(true);
              } else {
                localStorage.setItem(`map_stop_fields`, FieldMapping.toStorage(mapped));
                nextStep();
              }
            }}
            onBack={() => {
              previousStep();
            }}
          />
        )}
        {stage === `map_task_fields` && rawData && (
          <MapStopFields
            id="map_task_fields"
            rawData={rawData}
            tasksEnabled={shouldUseTasks}
            continueIntlId="data_importer_dialog.map_task_fields"
            fields={getTaskFields(activeProject)}
            externalIdMappingField={shouldUseTasks && stopMapping ? stopMapping.getById(`external_id`) : undefined}
            onCompleted={(mapped) => {
              setTasksMapping(mapped);
              localStorage.setItem(`map_task_fields`, FieldMapping.toStorage(mapped));
              nextStep();
            }}
            onBack={() => {
              previousStep();
            }}
          />
        )}
        {stage === `map_pickup_fields` && rawData && (
          <MapStopFields
            id="map_pickup_fields"
            rawData={rawData}
            continueIntlId="data_importer_dialog.map_pickup_fields"
            fields={getPickupFields({ project: activeProject, projectSupervisors, canViewSupervisors })}
            addressFields={getAddressFields()}
            onCompleted={(mapped) => {
              setPickupMapping(mapped);
              localStorage.setItem(`map_pickup_fields`, FieldMapping.toStorage(mapped));
              nextStep();
            }}
            onBack={() => {
              setShouldUseTasks(
                (activeProject?.planner.stopFieldsHasSpecificField(`external_id`) && activeProject?.tasks_enabled) ||
                  false
              );
              previousStep();
            }}
          />
        )}

        {stage === `geocode_addresses` && (
          <GeocodeAddresses
            createStopsList={CreateStopsList.create(stopMapping!, pickupMapping, tasksMapping)}
            onComplete={(createStopsList: CreateStopsList) => {
              setCreateStopsList(createStopsList);
              nextStep();
            }}
          />
        )}
        {stage === `review_addresses` && (
          <ReviewAddresses
            createStopsList={createStopsList!}
            tasksEnabled={shouldUseTasks}
            pickupsEnabled={shouldUsePickups}
            onComplete={(createStopsList: CreateStopsList) => {
              setCreateStopsList(createStopsList);
              nextStep();
            }}
            onBack={() => {
              previousStep();
            }}
          />
        )}
        {stage === `upload_data` && (
          <UploadStopsData
            createStopsList={createStopsList!}
            planId={activePlan?.id}
            onComplete={() => {
              eventsPanel.createStops(`Archivo`);
              // activePlanRefetch();
              onComplete();
              cleanState();
            }}
          />
        )}
      </Grid>

      <ConfirmDialog
        title={<Text variant="h5" intlId="data_importer_dialog.map_fields.skip_tasks_dialog.title" />}
        description={<Text intlId="data_importer_dialog.map_fields.skip_tasks_dialog.content" />}
        open={showSkipTasks}
        onCloseDialog={() => {
          setStopMapping(undefined);
          setShowSkipTasks(false);
        }}
        onConfirm={() => {
          setStopMapping(stopMapping);
          localStorage.setItem(`map_stop_fields`, FieldMapping.toStorage(stopMapping!));

          setTasksMapping(undefined);
          setShowSkipTasks(false);
          setShouldUseTasks(false);
        }}
      />
    </RoutalDialog>
  );
};
